import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getToken, clearToken, API_BASE_URL, DPP_BASE_URL } from '../dppApi';
import '../App.css';

const bloomMark = process.env.PUBLIC_URL + '/BloomFavicon.png';

const TRANSFER_TYPES = [
    { value: 'initial_sale', label: 'Initial Sale' },
    { value: 'resale', label: 'Resale' },
    { value: 'gift', label: 'Gift' },
    { value: 'inheritance', label: 'Inheritance' },
];

const BrandDashboard = () => {
    const navigate = useNavigate();
    const token = getToken('brand');
    const [products, setProducts] = useState([]);
    const [brand, setBrand] = useState(null);
    const [error, setError] = useState(null);
    const [transferPanel, setTransferPanel] = useState(null);
    const [certPanel, setCertPanel] = useState(null);

    const loadProducts = useCallback(async () => {
        try {
            setProducts(await apiFetch('/api/products', { token }));
        } catch (err) {
            setError(err.message);
        }
    }, [token]);

    useEffect(() => {
        if (!token) { navigate('/brand'); return; }
        loadProducts();
        apiFetch('/api/brand/me', { token }).then(setBrand).catch(() => {});
    }, [token, navigate, loadProducts]);

    const logout = () => { clearToken('brand'); navigate('/brand'); };

    const openCert = (productId, docType = 'certificate') =>
        setCertPanel({ productId, docType, file: null, issuedBy: '', issuedDate: '', busy: false, error: null, done: false });

    const closeCert = () => setCertPanel(null);

    const uploadDocument = async () => {
        if (!certPanel.file) return;
        setCertPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const form = new FormData();
            form.append('document', certPanel.file);
            form.append('doc_type', certPanel.docType);
            if (certPanel.issuedBy) form.append('issued_by', certPanel.issuedBy);
            if (certPanel.issuedDate) form.append('issued_date', certPanel.issuedDate);
            await apiFetch(`/api/products/${certPanel.productId}/documents`, { method: 'POST', token, body: form, isForm: true });
            setCertPanel(p => ({ ...p, busy: false, done: true }));
            loadProducts();
        } catch (err) {
            setCertPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const openTransfer = (productId) =>
        setTransferPanel({ productId, type: 'initial_sale', link: null, busy: false, error: null });

    const closeTransfer = () => setTransferPanel(null);

    const generateLink = async () => {
        setTransferPanel(p => ({ ...p, busy: true, error: null }));
        try {
            const data = await apiFetch(`/api/products/${transferPanel.productId}/sale-token`, {
                method: 'POST',
                token,
                body: { transfer_type: transferPanel.type },
            });
            const claimUrl = `${window.location.origin}/transfer/claim/${data.token_id}`;
            setTransferPanel(p => ({ ...p, busy: false, link: claimUrl }));
        } catch (err) {
            setTransferPanel(p => ({ ...p, busy: false, error: err.message }));
        }
    };

    const unsold = products.filter(p => p.ownership_count === 0);
    const sold = products.filter(p => p.ownership_count > 0);

    const renderProductCard = (p, showTransfer) => (
        <div className="collection-card" key={p.product_id}>
            {p.image_url && p.image_url.startsWith('http')
                ? <img className="collection-card-img" src={p.image_url} alt={p.name} />
                : <div className="collection-card-img collection-card-img--empty">No image</div>}

            <div className="collection-card-body">
                <div className="collection-card-title">{p.name}</div>
                <div className="collection-card-meta">{p.metal_type} {p.metal_purity}</div>

                <div className="collection-card-footer">
                    <div className="collection-card-actions">
                    <a className="collection-action" href={`${DPP_BASE_URL}/jewellery/${p.qr_code_id}`} target="_blank" rel="noreferrer">Open passport</a>
                    <button
                        className="collection-action"
                        onClick={() =>
                            certPanel?.productId === p.product_id && certPanel?.docType === 'certificate'
                                ? closeCert()
                                : openCert(p.product_id, 'certificate')
                        }
                    >
                        {certPanel?.productId === p.product_id && certPanel?.docType === 'certificate' ? 'Cancel' : 'Attach certificate'}
                    </button>
                    {!showTransfer && (
                        <button
                            className="collection-action"
                            onClick={() =>
                                certPanel?.productId === p.product_id && certPanel?.docType === 'receipt'
                                    ? closeCert()
                                    : openCert(p.product_id, 'receipt')
                            }
                        >
                            {certPanel?.productId === p.product_id && certPanel?.docType === 'receipt' ? 'Cancel' : 'Upload receipt'}
                        </button>
                    )}
                    {showTransfer && (
                        <button
                            className="collection-action"
                            onClick={() =>
                                transferPanel?.productId === p.product_id
                                    ? closeTransfer()
                                    : openTransfer(p.product_id)
                            }
                        >
                            {transferPanel?.productId === p.product_id ? 'Cancel' : 'Transfer'}
                        </button>
                    )}
                    </div>
                    <img className="collection-card-qr" src={`${API_BASE_URL}${p.qr_image_url}`} alt="QR code" />
                </div>

                {certPanel?.productId === p.product_id && (
                    <div className="dpp-transfer-panel">
                    {certPanel.done ? (
                        <p className="dpp-transfer-label" style={{ color: 'green' }}>
                            {certPanel.docType === 'receipt' ? 'Receipt uploaded.' : 'Certificate uploaded.'}
                        </p>
                    ) : (
                        <>
                            <label className="dpp-transfer-label">
                                {certPanel.docType === 'receipt' ? 'Receipt file' : 'Certificate file'}
                            </label>
                            <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={e => setCertPanel(p => ({ ...p, file: e.target.files[0] || null }))}
                            />
                            <label className="dpp-transfer-label">Issued by (optional)</label>
                            <input
                                className="dpp-input"
                                placeholder={certPanel.docType === 'receipt' ? 'e.g. Store name' : 'e.g. GIA'}
                                value={certPanel.issuedBy}
                                onChange={e => setCertPanel(p => ({ ...p, issuedBy: e.target.value }))}
                            />
                            <label className="dpp-transfer-label">Issued date (optional)</label>
                            <input
                                className="dpp-input"
                                type="date"
                                value={certPanel.issuedDate}
                                onChange={e => setCertPanel(p => ({ ...p, issuedDate: e.target.value }))}
                            />
                            {certPanel.error && <p className="dpp-error">{certPanel.error}</p>}
                            <button
                                className="dpp-button"
                                onClick={uploadDocument}
                                disabled={certPanel.busy || !certPanel.file}
                            >
                                {certPanel.busy ? 'Uploading…' : 'Upload'}
                            </button>
                        </>
                    )}
                </div>
            )}

            {showTransfer && transferPanel?.productId === p.product_id && (
                <div className="dpp-transfer-panel">
                    {!transferPanel.link ? (
                        <>
                            <label className="dpp-transfer-label">Transfer type</label>
                            <select
                                className="dpp-input"
                                value={transferPanel.type}
                                onChange={e => setTransferPanel(prev => ({ ...prev, type: e.target.value }))}
                            >
                                {TRANSFER_TYPES.map(({ value, label }) => (
                                    <option key={value} value={value}>{label}</option>
                                ))}
                            </select>
                            {transferPanel.error && <p className="dpp-error">{transferPanel.error}</p>}
                            <button className="dpp-button" onClick={generateLink} disabled={transferPanel.busy}>
                                {transferPanel.busy ? 'Generating…' : 'Generate transfer link'}
                            </button>
                        </>
                    ) : (
                        <>
                            <p className="dpp-transfer-label">Share this link with the new owner:</p>
                            <div className="dpp-transfer-link-row">
                                <input className="dpp-input" value={transferPanel.link} readOnly />
                                <button
                                    className="dpp-button"
                                    onClick={() => navigator.clipboard.writeText(transferPanel.link)}
                                >
                                    Copy
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}
            </div>
        </div>
    );

    return (
        <div className="dpp-page">
            <div className="collection-topbar">
                <div className="collection-topbar-brand">
                    <img className="dpp-brand-mark" src={bloomMark} alt="Bloom" />
                    <h1 className="collection-topbar-title">Brand Dashboard</h1>
                </div>
                <div className="dpp-header-actions">
                    <button className="dpp-button" onClick={() => navigate('/brand/products/new')}>+ New product</button>
                    <button className="dpp-link-button" onClick={logout}>Log out</button>
                </div>
            </div>

            {error && <p className="dpp-error" style={{ maxWidth: 960, margin: '0 auto 16px' }}>{error}</p>}

            <div className="dpp-brand-profile-card dpp-card">
                <div
                    className="dpp-brand-header-logo-box"
                    style={brand?.header_logo_bg_color
                        ? { backgroundColor: brand.header_logo_bg_color }
                        : undefined}
                >
                    {brand?.header_logo_url &&
                        <img className="dpp-brand-header-logo-img" src={brand.header_logo_url} alt="Brand header logo" />
                    }
                </div>
            </div>

            <h2 className="collection-section-title" style={{ maxWidth: 880 }}>Your products ({unsold.length})</h2>
            {unsold.length === 0
                ? <p className="collection-status" style={{ maxWidth: 880 }}>No unsold products.</p>
                : <div className="collection-grid">{unsold.map(p => renderProductCard(p, true))}</div>}

            {sold.length > 0 && (
                <>
                    <h2 className="collection-section-title" style={{ marginBottom: 28, maxWidth: 880 }}>Sold products ({sold.length})</h2>
                    <div className="collection-grid">{sold.map(p => renderProductCard(p, false))}</div>
                </>
            )}

            <footer className="site-footer">© {new Date().getFullYear()} Bloom. All rights reserved.</footer>
        </div>
    );
};

export default BrandDashboard;
